<script lang="ts">
	import SvgLoader from '#lib/components/SvgLoader.svelte';
	import { twMerge } from 'tailwind-merge';
	import type { HDGameReportResponseData } from '../types/responses/GameReport.js';
	import { GameStatus } from '../types/util/GameStatus.js';
	import dayjs from 'dayjs';
	import HdGameReportCountdown from './HDGameReportCountdown.svelte';
	import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
	import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
	import { type HDScheduleStream } from '../types/responses/Schedule.js';

	let { game }: { game: HDGameReportResponseData } = $props();

	let hasSOG = $derived(
		game.gameData.gameStatus >= GameStatus.LIVE
			? game.gameData.periodStats.some(
					(period) => period.homeShotsOnGoal > 0 || period.awayShotsOnGoal > 0
				)
			: false
	);
</script>

<div class="flex w-full justify-center pb-3">
	<div
		class="bg-base-100 relative isolate flex min-h-[350px] w-full min-w-0 flex-col gap-6 overflow-hidden p-4 sm:p-6 lg:flex-row"
	>
		<div class="flex min-w-0 flex-col lg:w-4/12">
			<div class="flex w-full flex-col">
				<div
					class="text-shadow shadow-primary text-base-100 text-5xl font-bold tracking-wider uppercase"
				>
					{#if game.gameData.gameStatus === GameStatus.LIVE}
						Live
					{:else if game.gameData.gameStatus === GameStatus.ENDED}
						Final
					{:else}
						Match
					{/if}
				</div>
				<div class="text-5xl font-bold tracking-wider uppercase">
					{#if game.gameData.gameStatus >= GameStatus.LIVE}
						Score
					{:else}
						Countdown
					{/if}
				</div>
			</div>
			<div class="border-accent mt-5 flex w-full flex-col border-l-[14px]">
				<div class="pl-3 text-2xl font-bold">
					{dayjs(game.gameData.scheduledDate.sortValue).format('dddd')}
				</div>
				<div class="pl-3 text-3xl font-bold uppercase">
					{dayjs(game.gameData.scheduledDate.sortValue).format('DD.MM.YYYY')}
				</div>
				<div
					class="text-shadow shadow-primary text-base-100 pl-3 text-4xl font-bold tracking-wider uppercase"
				>
					{game.gameData.scheduledTime}
				</div>
				<div class="mt-3 pl-3 text-2xl font-bold">
					{game.gameData.location.longname}
				</div>
			</div>
			<div class="mt-4 flex flex-row items-center justify-start gap-2">
				{#if game.gameData.streams && game.gameData.streams.length > 0}
					{#each game.gameData.streams as stream}
						{@const streamData = JSON.parse(stream) as HDScheduleStream}
						<a
							href={streamData.targetUrl}
							target={streamData.target}
							rel="noopener noreferrer"
							class="text-base-content hover:bg-base-200 inline-flex min-h-11 items-center gap-1 border-2 border-red-500/50 px-2 font-semibold uppercase hover:bg-red-500/50"
							onclick={(e) => {
								e.stopPropagation();
							}}
						>
							{streamData.label || 'Livestream'}
							<FontAwesomeIcon icon={faExternalLinkAlt} class="ml-1 h-3 w-3" />
						</a>
					{/each}
				{/if}
			</div>
		</div>
		<div class="flex w-full min-w-0 flex-col items-end justify-end gap-3 lg:w-8/12">
			{#if game.gameData.gameStatus >= GameStatus.LIVE}
				<div
					class={[
						'flex w-full items-center justify-end gap-5',
						game?.gameData.homeTeamScore > game?.gameData.awayTeamScore
							? 'text-accent'
							: 'text-primary'
					]}
				>
					<div class="flex min-w-0 flex-grow flex-col items-end">
						<div
							class="flex w-full justify-end text-end text-2xl font-bold wrap-anywhere sm:text-4xl"
						>
							{game.gameData.homeTeamShortname}
						</div>
						<div class="flex w-full justify-end text-end text-xl">
							{game.gameData.homeTeamLongname}
						</div>
					</div>
					<div class="bg-neutral flex h-16 w-16 shrink-0 items-center justify-center">
						<div class="text-4xl font-bold">
							{game?.gameData.homeTeamScore ?? 0}
						</div>
					</div>
				</div>
				<div
					class={[
						'flex w-full items-center justify-end gap-5',
						game?.gameData.awayTeamScore > game?.gameData.homeTeamScore
							? 'text-accent'
							: 'text-primary'
					]}
				>
					<div class="flex min-w-0 flex-grow flex-col items-end text-end">
						<div
							class="flex w-full justify-end text-end text-2xl font-bold wrap-anywhere sm:text-4xl"
						>
							{game.gameData.awayTeamShortname}
						</div>
						<div class="flex w-full justify-end text-end text-xl">
							{game.gameData.awayTeamLongname}
						</div>
					</div>
					<div class="bg-neutral flex h-16 w-16 shrink-0 items-center justify-center">
						<div class="text-4xl font-bold">
							{game?.gameData.awayTeamScore ?? 0}
						</div>
					</div>
				</div>
				<div
					class="bg-base-100 flex max-w-full flex-row items-center justify-start overflow-x-auto"
				>
					<div class="flex flex-col rounded-l-lg text-right">
						<div class="border-accent/50 border-b-2 px-2 font-bold">Abschnitt</div>
						<div class="px-2 font-bold">Tore</div>
						<div class={twMerge('px-2 font-bold', hasSOG ? '' : 'text-neutral')}>Torschüsse</div>
					</div>
					{#each game.gameData.periodStats as period (period.period)}
						<div class="flex flex-col text-center">
							<div class="border-accent/50 border-b-2 px-2 font-bold">
								{period.period}
							</div>
							<div class="px-2">{period.homeScore}&nbsp;:&nbsp;{period.awayScore}</div>
							<div class={twMerge('px-2', hasSOG ? '' : 'text-neutral')}>
								{period.homeShotsOnGoal}&nbsp;:&nbsp;{period.awayShotsOnGoal}
							</div>
						</div>
					{/each}
				</div>
			{:else}
				<HdGameReportCountdown {game} />
			{/if}
		</div>

		<div
			aria-hidden="true"
			class="pointer-events-none absolute top-2 right-0 -z-10 translate-x-1/3 opacity-30"
		>
			<SvgLoader name="stripes" styleClass="fill-accent h-20 w-auto object-cover" />
		</div>
	</div>
</div>
